import { useEffect, useMemo } from 'react';

import {
  SecondaryNavIdentifier,
  TertiaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import {
  SearchableVendorTaxonomyKey,
  SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { AccountIncentiveStatusView } from '@zola/svc-marketplace-ts-types';
import { ResponsiveDesignProvider } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import _isEmpty from 'lodash/isEmpty';
import { GetServerSideProps } from 'next';
import queryString from 'query-string';
import { Provider } from 'react-redux';

import MainLayout from '~/components/common/layouts/MainLayout/MainLayout';
import { getBookedVendorsAllowsMiniQuiz } from '~/components/common/modals/MiniQuizModal/hooks/useBookedVendorsAllowMiniQuiz';
import { AccountIncentivizationProvider } from '~/contexts/AccountIncentivizationContext';
import { UserContextProvider, useUserContext } from '~/contexts/UserContext';
import { findSlugsInPath } from '~/hooks/usePathSlugs';
import { getAccountIncentives } from '~/libs/server/api/accountIncentives';
import { getNavHtml } from '~/libs/server/api/nav';
import {
  fetchStorefrontSearchResults,
  fetchZolaTopPicksResults,
} from '~/libs/server/api/storefronts';
import { getSearchableFacets, getTaxonomyNodes } from '~/libs/server/api/taxonomy';
import { getUserContext } from '~/libs/server/api/user';
import { fetchVendorSearchResultsUserData } from '~/libs/server/api/vendorSearch';
import ApiService from '~/libs/server/util/apiService';
import { getSearchLocationForSlug } from '~/libs/universal/vendorMarket';
import { fetchVendorSearchResultsPageData } from '~/libs/universal/vendorSearch';
import { getMiniQuizUserSettingForTaxonomy } from '~/pages/couples/explore/components/MiniQuiz/miniQuizUserSettings';
import {
  SearchResultPageSearchResult,
  ShouldInsertMiniQuizByTaxonomy,
} from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import {
  getVendorTaxonomiesByKey,
  SearchResultsPageProvider,
  SearchResultsPageProviderProps,
} from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageProvider';
import { PostAuthSearchResultsPage } from '~/pages/couples/explore/components/SearchResults/PostAuthSearchResultsPage';
import { PreAuthSearchResultsPage } from '~/pages/couples/explore/components/SearchResults/PreAuthSearchResultsPage';
import { buildBackendVendorSearchRequestFromSearchProperties } from '~/pages/couples/explore/util/buildSearchRequest';
import {
  locationSlugInPath,
  parseVendorSearchUrl,
  ParseVendorSearchUrlResult,
} from '~/pages/couples/explore/util/parseVendorSearchUrl';
import { reduceTaxonomyWithFacetsToLookup } from '~/reducers/ui/couplesSearch';
import { getStore } from '~/store';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { IUserContext } from '~/types/userContext';
import EXPERIMENT_FLAGS, { EXPERIMENT_FLAGS_SSR } from '~/util/experimentFlags';
import {
  ALL_MARKETS,
  MostRecentVendorSearches,
  VENDOR_MOST_RECENT_SEARCH_URL_BY_VENDOR_TYPE_KEY,
} from '~/util/searchUtils';
import { isVendorIdentified } from '~/util/vendorIdentification';

import styles from './slug.module.less';

export type SearchResultsPageProps = SearchResultsPageProviderProps & {
  navHtml: string | null;
  userAgent: string | undefined;
  incentivizationStatus: AccountIncentiveStatusView[];
  parsedUrl: ParseVendorSearchUrlResult;
};

const PageWithUserContext = ({
  userAgent,
  incentivizationStatus,
  navHtml,
  identifiedAsVendor,
  forcePreAuthSrp,
  ...rest
}: SearchResultsPageProps & { forcePreAuthSrp: boolean }) => {
  const { isPreAuthSrp, parsedUrl } = rest;
  const userContext = useUserContext();
  const store = useMemo(() => getStore(), []);

  return (
    <AccountIncentivizationProvider
      incentiveStatus={incentivizationStatus}
      userContext={userContext}
    >
      <ResponsiveDesignProvider userAgent={userAgent}>
        <Provider store={store}>
          <MainLayout
            activeSecondaryLink={SecondaryNavIdentifier.EXPLORE_VENDORS}
            activeTertiaryLink={
              parsedUrl.vendorSearch.selectedTaxonomy?.key as TertiaryNavIdentifier
            }
            ssrNavHtml={navHtml}
            className={styles.mainLayout}
            id="main"
          >
            <SearchResultsPageProvider
              {...rest}
              identifiedAsVendor={identifiedAsVendor}
              parsedUrl={parsedUrl}
            >
              {isPreAuthSrp || forcePreAuthSrp ? (
                <PreAuthSearchResultsPage />
              ) : (
                <PostAuthSearchResultsPage />
              )}
            </SearchResultsPageProvider>
          </MainLayout>
        </Provider>
      </ResponsiveDesignProvider>
    </AccountIncentivizationProvider>
  );
};

const Page = ({
  userContext,
  ...rest
}: SearchResultsPageProps & { userContext: IUserContext; forcePreAuthSrp: boolean }) => {
  const { identifiedAsVendor } = rest;

  useEffect(() => {
    // Trigger experiment viewed
    if (!identifiedAsVendor) {
      EXPERIMENT_FLAGS.isUnderTest('marketplace_featured_vendors_holdout', userContext, true);
    }
  }, [identifiedAsVendor, userContext]);

  useEffectOnce(() => {
    // Trigger experiment viewed
    EXPERIMENT_FLAGS.isUnderTest('marketplace-preauth-srp-v2', userContext, true);
  });

  return (
    <UserContextProvider userContext={userContext}>
      <PageWithUserContext {...rest} />
    </UserContextProvider>
  );
};

const getLocationFromUrl = (slug: string | null): Promise<LocationOrAllMarketsType | null> => {
  const pathSlugs = findSlugsInPath(slug ?? '');
  const locationSlug = locationSlugInPath(pathSlugs);

  if (locationSlug) {
    return getSearchLocationForSlug(locationSlug, ApiService);
  }

  return Promise.resolve(ALL_MARKETS);
};

export const getServerSideProps: GetServerSideProps<SearchResultsPageProps> = async (context) => {
  const slug = Array.isArray(context.query.slug) ? context.query.slug[0] : context.query.slug ?? '';

  // Fetch the properties that are 100% static and have no dependencies.
  const [navHtml, userContext, taxonomyNodes, taxonomiesWithFacets, selectedLocation] =
    await Promise.all([
      getNavHtml(context.req),
      getUserContext(context.req),

      // taxonomyNodes: All the taxonomy nodes (venues/etc)
      getTaxonomyNodes().then((nodes) => {
        return [...nodes].sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }

          return 0;
        });
      }),

      // taxonomiesWithFacets: this drives the filters on the page
      getSearchableFacets(),

      // selectedLocation: The location parsed out of the slug and verified with the backend as valid
      getLocationFromUrl(slug),
    ]);

  // Transform the taxonomies (with their allowed facets) into a couple of static
  // lookups that let us easily find the facets for a given taxonomy or taxonomy/category
  const { facetsByVendorType, facetsByVendorTypeAndCategory } =
    reduceTaxonomyWithFacetsToLookup(taxonomiesWithFacets);

  // Transform the taxonomy nodes into a lookup so we can validate `wedding-venues` from the slug is allwoed
  const taxonomyNodesByKey = getVendorTaxonomiesByKey(taxonomyNodes);

  // Parse the URL out into its parts: the query string and the path
  const { query, url: pathname } = queryString.parseUrl(context.req.url || '', {
    arrayFormat: 'bracket',
  });

  const isPreAuthSrp = Boolean(userContext?.is_guest);

  // Parse the URL into a more usable format: the vendorSearch, the canonical url,
  // and all the selected facets.  This is the main driver of the page (the URL is
  // the source of truth for all the inputs to search).  This validates the slugs
  // in the URL and lets us know if anything are invalid (via the canonicalUrl)
  const parsedUrl = parseVendorSearchUrl({
    slug: slug,
    query: query,
    taxonomyNotesByKey: taxonomyNodesByKey,
    facetsByVendorType,
    selectedLocation,
    searchName: isPreAuthSrp ? 'PRE_AUTH_SRP' : null,
  });

  let parsedUrlToUse = parsedUrl;
  const isSRPLinkExperiment =
    !!userContext &&
    !userContext.is_guest &&
    EXPERIMENT_FLAGS_SSR.getExperimentVariation(
      'marketplace-clp-lp-links-to-srp',
      userContext,
      context.req.cookies
    ) === 'TEST-1';
  const selectedTaxonomyKey = parsedUrl.vendorSearch.selectedTaxonomy?.key;

  const mostRecentSearchUrlsByVendorType =
    context.req.cookies[VENDOR_MOST_RECENT_SEARCH_URL_BY_VENDOR_TYPE_KEY];
  const parsedSearchUrls = mostRecentSearchUrlsByVendorType
    ? (JSON.parse(mostRecentSearchUrlsByVendorType) as MostRecentVendorSearches)
    : {};

  if (isSRPLinkExperiment && selectedTaxonomyKey && parsedSearchUrls) {
    const savedSearchUrl = parsedSearchUrls[selectedTaxonomyKey];

    // if there is a cookied search URL for the selected taxonomy key, get a new parsed URL
    // note that while the entire search URL is saved in cookies, the location is not actually used
    // i.e. if a search of washington-dc--wedding-photographers--classic is saved in cookies and the user next visits the photographer SRP with a Boston location
    // the classic filter will be applied to the new search in boston (we will never overwrite location)
    if (savedSearchUrl) {
      const { query: cookieQuery, url: cookiePathname } = queryString.parseUrl(savedSearchUrl, {
        arrayFormat: 'bracket',
      });

      parsedUrlToUse = parseVendorSearchUrl({
        slug: cookiePathname,
        query: cookieQuery,
        taxonomyNotesByKey: taxonomyNodesByKey,
        facetsByVendorType,
        selectedLocation,
        searchName: isPreAuthSrp ? 'PRE_AUTH_SRP' : null,
      });
    }
  }

  // Here is where we find out of any of the parameters in the URL were invalid
  // and we redirect to the canonical URL if they were.
  const queryStringParsedUrl = queryString.parseUrl(parsedUrlToUse.canonicalUrl);
  if (
    parsedUrlToUse.canonicalUrl &&
    (queryStringParsedUrl.url !== pathname ||
      (isSRPLinkExperiment && !_isEmpty(queryStringParsedUrl.query) && _isEmpty(query)))
  ) {
    return {
      redirect: {
        destination: parsedUrlToUse.canonicalUrl,
        permanent: false,
      },
    };
  }

  const preAuthV2Additions = Boolean(
    userContext?.is_guest &&
      ['TEST', 'TEST-0', 'TEST-1'].includes(
        EXPERIMENT_FLAGS_SSR.getExperimentVariation(
          'marketplace-preauth-srp-features-release-ramp',
          userContext,
          context.req.cookies || {}
        )
      )
  );
  const initialCategory = parsedUrlToUse.vendorSearch.selectedTaxonomy?.key;

  // Now that we have the search (parsedUrlToUse.vendorSearch) we can execute queries
  // that are related to search.  Some of these are static for the page and some
  // are specific to the user.
  const [
    [
      supplied,
      adjacentVendors,
      relatedLinks,
      realWeddings,
      expertAdvice,
      seoContent,
      seoTopLocations,
      seoNearbyCities,
      searchLocationReviews,
    ],
    [
      userSettings,
      accountVendors,
      vendorPreferences,
      weddingPreferences,
      showTopPicks,
      topPicks,
      recentlyViewedByTaxonomy,
      storefrontToInquiryMap,
      hasVenueBudget,
    ],
    incentivizationStatus,
  ] = await Promise.all([
    // Static data for the page based on the city + taxonomy (and facets selected)
    fetchVendorSearchResultsPageData(parsedUrlToUse.vendorSearch, ApiService, preAuthV2Additions),

    // Static data for the page based on the _user_ (and the search)
    fetchVendorSearchResultsUserData(parsedUrlToUse.vendorSearch, userContext, context.req),

    // Other static data needed for the page
    getAccountIncentives(userContext, context.req),
  ]);

  // Determine if a vendor is viewing the page or if the couple is in the holdout
  // group.  For couples in the holdout group: they won't see featured vendors.
  // Since vendors have _paid for featured slots_ we want to always show them
  // featured vendors
  const identifiedAsVendor = isVendorIdentified((key: string) =>
    Boolean((context.req.cookies || {})[key])
  );

  const isInHoldoutGroupExperiment =
    EXPERIMENT_FLAGS_SSR.getExperimentVariation(
      'marketplace_featured_vendors_holdout',
      userContext,
      context.req.cookies || {}
    ) === 'TEST-1';

  const isPreAuthSrpTest =
    EXPERIMENT_FLAGS_SSR.getExperimentVariation(
      'marketplace-preauth-srp-v2',
      userContext,
      context.req.cookies || {}
    ) === 'TEST-1';

  // Determine if the mini-quiz should be inserted (BY CATEGORY)
  // This is a static, one time lookup.  If the mini-quiz is answered
  // we need to hard reload the page to get a new lookup.
  const shouldInsertMiniQuiz: ShouldInsertMiniQuizByTaxonomy =
    SEARCHABLE_VENDOR_TAXONOMY_KEYS.reduce((result, taxonomyKey) => {
      const bookedVendorsAllowsQuiz = getBookedVendorsAllowsMiniQuiz(accountVendors, taxonomyKey);
      const userSettingKey = getMiniQuizUserSettingForTaxonomy(taxonomyKey);

      const userSetting = userSettingKey ? userSettings[userSettingKey] : undefined;

      const userSettingsAllowedQuiz = !(userSetting ? userSetting.flag : false);
      const shouldInsertQuizForCategory = bookedVendorsAllowsQuiz && userSettingsAllowedQuiz;

      return {
        ...result,
        [taxonomyKey]: !isPreAuthSrp && shouldInsertQuizForCategory,
      };
    }, {});

  // Determine if the mini-quiz should be inserted on page load (for the current category)
  const shouldInsertMiniQuizThisCategory = Boolean(
    initialCategory && shouldInsertMiniQuiz[initialCategory as SearchableVendorTaxonomyKey]
  );

  // Determine if the BUDGET quiz should be inserted.  This is only for venues.
  // Like with the mini-quiz, this is a one-time static determination.
  const shouldInsertBudgetQuiz =
    !isPreAuthSrp && initialCategory === VENUES_TAXONOMY_KEY && !hasVenueBudget;

  // Build and execute the search request.  This is finally running the vendor
  // search and getting us StorefrontCardViews we can render on the page.
  const vendorSearchRequest = buildBackendVendorSearchRequestFromSearchProperties({
    vendorSearch: parsedUrlToUse.vendorSearch,
    shouldInsertBudgetQuiz,
    shouldInsertMiniQuiz: shouldInsertMiniQuizThisCategory,
    identifiedAsVendor,
    isInHoldoutGroupExperiment,
  });

  const [searchResponse, zolaTopPicks] = await Promise.all([
    fetchStorefrontSearchResults(vendorSearchRequest, context.req),
    preAuthV2Additions
      ? fetchZolaTopPicksResults(parsedUrlToUse.vendorSearch, context.req)
      : Promise.resolve([]),
  ]);

  const vendorsAndLoad: Pick<SearchResultPageSearchResult, 'vendors'> = {
    vendors: searchResponse.entities,
  };

  // Transform the search response into the form used on the page.
  const searchResults: SearchResultPageSearchResult = {
    totalHits: searchResponse.totalHits,
    offset: searchResponse.offset,
    shouldInsertMiniQuiz: shouldInsertMiniQuizThisCategory,
    shouldInsertBudgetQuiz,
    ...vendorsAndLoad,
  };

  const forcePreAuthSrp =
    EXPERIMENT_FLAGS_SSR.getExperimentVariation(
      'marketplace-use-preauth-srp-for-post-auth-user',
      userContext,
      context.req.cookies || {}
    ) === 'TEST-1';

  return {
    props: {
      userContext,
      navHtml,
      parsedUrl: parsedUrlToUse,
      userAgent: context.req.headers['user-agent'],
      facetsByVendorType,
      facetsByVendorTypeAndCategory,
      seoContent,
      seoTopLocations,
      seoNearbyCities,
      searchLocationReviews,
      supplied,
      adjacentVendors,
      relatedLinks,
      realWeddings,
      expertAdvice,
      userSettings,
      accountVendors,
      vendorPreferences,
      weddingPreferences,
      showTopPicks,
      topPicks,
      identifiedAsVendor,
      isInHoldoutGroupExperiment,
      recentlyViewedByTaxonomy,
      storefrontToInquiryMap,
      hasVenueBudget,
      shouldInsertMiniQuiz,
      searchResults,
      isPreAuthSrp,
      isPreAuthSrpTest,
      incentivizationStatus,
      zolaTopPicks,
      preAuthV2Additions,
      isSRPLinkExperiment,
      forcePreAuthSrp,
    },
  };
};

export default Page;
